import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../ForBrands/brandMode.css';
import { BackLink, ConfirmModal, PageHeader } from '../ForBrands/ui/BrandUI';
import TeamGamesPage from '../ForBusiness/GameStats/TeamGamesPage';
import { adminGamesUrl, makeGameStatsApi } from '../ForBusiness/GameStats/gameStatsServices';
import { teamPath } from './teamPaths';

// "Sierra Canyon · Mission League": the team the sheet is for, under the
// title. No season here: the sheet has its own season picker.
export const teamLine = (team) => [team?.name || team?.organization, team?.league_label || team?.partner_program]
  .map((part) => (typeof part === 'string' ? part.trim() : ''))
  .filter(Boolean)
  .join(' · ');

// Admin Mode > Teams > a partner team's game stats
// (/admin/team-management/<team_id>/games): the shared game sheet
// (ForBusiness/GameStats) under the Admin Mode head, with a way back to the
// team on the Teams page. Admins only for now, through the /admin route
// group; Coach Mode can mount the same sheet later under its own head.
const TeamGamesAdminPage = () => {
  const { teamId = '' } = useParams();
  const navigate = useNavigate();
  // The sheet's own admin services, with the team its list answer carries
  // kept for the head, so one request loads the sheet and names the team.
  const [named, setNamed] = useState({ teamId: '', line: '' });
  const api = useMemo(() => {
    const base = makeGameStatsApi({ baseUrl: adminGamesUrl(teamId) });
    return {
      ...base,
      list: async (season) => {
        const res = await base.list(season);
        const line = teamLine(res?.team);
        if (line) setNamed((prev) => (prev.teamId === teamId && prev.line === line ? prev : { teamId, line }));
        return res;
      },
    };
  }, [teamId]);
  // The sheet says when its editor holds unsaved edits. The Back link then
  // asks first, the way the sheet's own Cancel does (the sheet already asks
  // before a reload or a closed tab).
  const [dirty, setDirty] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const back = teamPath(teamId);
  const header = (
    <div className="flex flex-col pt-6">
      <BackLink to={back} onClick={dirty ? () => setLeaving(true) : undefined}>Teams</BackLink>
      <PageHeader
        eyebrow="Admin Mode"
        title="Game stats"
        subtitle={named.teamId === teamId ? named.line : ''}
      />
    </div>
  );
  // Keyed on the team, so another team's sheet starts from a clean slate.
  return (
    <div className="w-full min-w-0 max-w-full">
      <TeamGamesPage key={teamId} teamId={teamId} api={api} header={header} onDirtyChange={setDirty} />
      {leaving && (
        <ConfirmModal
          title="Leave without saving?"
          body="Your changes to this game are not saved yet."
          confirmLabel="Leave without saving"
          cancelLabel="Keep editing"
          requireAck={false}
          onConfirm={() => navigate(back)}
          onClose={() => setLeaving(false)}
        />
      )}
    </div>
  );
};

export default TeamGamesAdminPage;
